import { makeStyles } from '@material-ui/core/styles';
import { buttonIconStyle } from '../styles/buttons.styles';
import { redesignTheme } from '../../theme';
import { desktopOnlyAt, headerSharedStyles } from './Header.styles';

const { colors, fonts, fontSize, spacing, radius, breakpoints, sizes } = redesignTheme;

export type AppHeaderStyleProps = {
  navCollapseAt: number;
};

export const useAppHeaderStyles = makeStyles({
  headerShell: {
    position: 'sticky',
    top: 0,
    zIndex: 1302,
    overflow: 'visible',
  },

  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    minHeight: 56,
    padding: `12px ${spacing.md}px`,
    backgroundColor: colors.white,
    borderBottom: `1px solid ${colors.surfaceBorder}`,
    boxSizing: 'border-box',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      padding: `12px ${spacing.sm}px`,
      gap: spacing.xs,
    },
  },

  // Figma Header_Mobile_Fermé: hamburger | centered logo | language
  rootWithMobileNav: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    [`@media (max-width: ${navCollapseAt}px)`]: {
      gap: 38,
      minHeight: 68,
      padding: spacing.md,
      borderBottom: `1px solid ${colors.border}`,
    },
  }),

  logoButton: {
    border: 0,
    padding: 0,
    background: 'transparent',
    cursor: 'pointer',
    flexShrink: 0,
  },

  logoButtonMobileNav: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    [`@media (max-width: ${navCollapseAt}px)`]: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      minWidth: 0,
    },
  }),

  logo: {
    display: 'block',
    width: sizes.logoWidth,
    height: sizes.logoHeight,
    maxWidth: '40vw',
    objectFit: 'contain',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      width: 160,
      height: 'auto',
    },
  },

  logoMobileNav: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    [`@media (max-width: ${navCollapseAt}px)`]: {
      width: 220,
      height: 'auto',
      maxWidth: '100%',
    },
  }),

  actions: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
    [`@media (max-width: ${navCollapseAt}px)`]: {
      gap: spacing.xs,
    },
  }),

  divider: {
    width: 1,
    height: 20,
    backgroundColor: colors.border,
    flexShrink: 0,
  },

  dividerDesktopOnly: ({ navCollapseAt }: AppHeaderStyleProps) => desktopOnlyAt(navCollapseAt),

  boutiqueButton: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    ...headerSharedStyles.actionButtonBase,
    ...desktopOnlyAt(navCollapseAt),
  }),

  boutiqueIcon: {
    ...buttonIconStyle,
  },

  matchButton: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    ...headerSharedStyles.actionButtonBase,
    ...desktopOnlyAt(navCollapseAt),
  }),

  matchIcon: {
    ...buttonIconStyle,
  },

  flagSelectButton: {
    border: 'none !important',
    backgroundColor: 'transparent !important',
    padding: '0 !important',
    gap: `${spacing.xs}px !important`,
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },

  flagSelectPopper: {
    ...headerSharedStyles.popper,
  },

  profileWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },

  profileDesktopOnly: ({ navCollapseAt }: AppHeaderStyleProps) => desktopOnlyAt(navCollapseAt),

  profileButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 12,
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: fonts.opensans,
    color: colors.overlay,
  },

  profileAvatar: {
    width: 32,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    backgroundColor: colors.primaryDark,
    color: colors.white,
    fontSize: fontSize.sm,
    fontFamily: fonts.opensansBold,
    letterSpacing: 0.24,
    overflow: 'hidden',
    flexShrink: 0,
  },

  profilePhoto: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  profileName: {
    fontSize: fontSize.md,
    fontFamily: fonts.opensans,
    lineHeight: 1.6,
    whiteSpace: 'nowrap',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      display: 'none',
    },
  },

  profileChevron: {
    width: 16,
    height: 16,
    color: colors.overlay,
    flexShrink: 0,
  },

  recruitmentButton: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    height: 35,
    padding: '0 12px',
    border: `1px solid ${colors.primaryDark}`,
    borderRadius: 12,
    backgroundColor: colors.white,
    color: colors.primaryDark,
    cursor: 'pointer',
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.sm,
    whiteSpace: 'nowrap',
    [`@media (max-width: ${navCollapseAt}px)`]: {
      display: 'none',
    },
  }),

  mobileNavButton: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    border: 0,
    borderRadius: 0,
    backgroundColor: 'transparent',
    color: colors.primaryDark,
    cursor: 'pointer',
    flexShrink: 0,
    [`@media (max-width: ${navCollapseAt}px)`]: {
      display: 'inline-flex',
    },
  }),

  mobileNavIcon: {
    width: 24,
    height: 24,
  },

  mobileNavBackdrop: {
    display: 'none',
  },

  mobileNavHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 70,
    height: 68,
    padding: spacing.md,
    borderBottom: `1px solid ${colors.border}`,
    boxSizing: 'border-box',
    '& $logoButton, & $logoButtonMobileNav': {
      flex: 1,
      display: 'flex',
      justifyContent: 'flex-start',
      minWidth: 0,
    },
    '& $logo, & $logoMobileNav': {
      width: 220,
      height: 'auto',
      maxWidth: '100%',
    },
  },

  mobileNavHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    flexShrink: 0,
  },

  mobileNavCloseButton: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: colors.label,
    cursor: 'pointer',
    '& svg': {
      width: 24,
      height: 24,
    },
    [`@media (min-width: ${navCollapseAt + 1}px)`]: {
      display: 'none',
    },
  }),

  mobileNavPanel: ({ navCollapseAt }: AppHeaderStyleProps) => ({
    position: 'fixed',
    inset: 0,
    zIndex: 1303,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.white,
    [`@media (min-width: ${navCollapseAt + 1}px)`]: {
      display: 'none',
    },
  }),

  mobileNavList: {
    flex: 1,
    listStyle: 'none',
    margin: 0,
    padding: 0,
    overflowY: 'auto',
  },

  mobileNavListItem: {
    margin: 0,
  },

  mobileNavItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: 70,
    padding: '20px 24px',
    border: 0,
    borderLeft: '2px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: fonts.opensans,
    fontSize: fontSize.base,
    color: colors.primaryDark,
    lineHeight: 'normal',
    textAlign: 'left',
    boxSizing: 'border-box',
  },

  mobileNavItemActive: {
    fontFamily: fonts.opensansSemiBold,
    borderLeftColor: colors.primaryDark,
  },

  mobileNavChevron: {
    width: 20,
    height: 20,
    color: colors.label,
    flexShrink: 0,
  },

  mobileNavFooter: {
    marginTop: 'auto',
  },

  mobileNavBoutiqueWrap: {
    padding: spacing.md,
    borderTop: `1px solid ${colors.border}`,
  },

  mobileNavBoutiqueButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    width: '100%',
    height: 48,
    margin: 0,
    padding: '8px 52px',
    border: 0,
    borderRadius: 12,
    backgroundColor: colors.primaryDark,
    color: colors.white,
    cursor: 'pointer',
    fontFamily: fonts.opensans,
    fontSize: fontSize.sm,
    boxSizing: 'border-box',
  },

  mobileNavProfile: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: spacing.md,
    borderTop: `1px solid ${colors.border}`,
  },

  mobileProfileButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: colors.overlay,
    cursor: 'pointer',
    fontFamily: fonts.opensans,
  },

  mobileProfileName: {
    fontSize: fontSize.base,
    lineHeight: 1.6,
    whiteSpace: 'nowrap',
  },

  mobileProfileMenu: {
    position: 'absolute',
    bottom: 68,
    left: 40,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    width: 192,
    padding: spacing.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    backgroundColor: colors.white,
    boxShadow: '0 4px 16px rgba(14, 18, 71, 0.08)',
    boxSizing: 'border-box',
  },

  mobileProfileMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: colors.textBody,
    cursor: 'pointer',
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    lineHeight: 'normal',
    textAlign: 'left',
  },

  mobileProfileMenuIcon: {
    width: 20,
    height: 20,
    color: colors.textBody,
    flexShrink: 0,
  },
});
